<script setup lang="ts">
import { computed } from 'vue';
import SingleOperationOfficeAutoComplete from '@/components/AutoCompletes/SingleOperationOfficeAutoComplete.vue';
import DepartmentAutoComplete from '@/components/AutoCompletes/DepartmentAutoComplete.vue';
import VehicleAutoComplete from '@/components/AutoCompletes/VehicleAutoComplete.vue';
import RangeDatePicker from '@/components/Common/RangeDatePicker.vue';
import i18n from '@/lang/i18n';
import type getOperationOffice from '@/types/responses/getOperationOffice';
import type getDepartmentResponse from '@/types/responses/getDepartment';

const props = defineProps<{
  selectedOperationOffice: getOperationOffice | null;
  selectedDepartment: getDepartmentResponse | undefined;
  selectedVehicle: any | null;
  dateRange: Date[];
  loading: boolean;
  maxDateRange: number;
}>();

const emit = defineEmits<{
  'update:selectedOperationOffice': [value: getOperationOffice | null];
  'update:selectedDepartment': [value: getDepartmentResponse | undefined];
  'update:selectedVehicle': [value: any | null];
  'update:dateRange': [value: Date[]];
  'search': [];
}>();

const selectedOperationOffice = computed({
  get: () => props.selectedOperationOffice,
  set: (val) => emit('update:selectedOperationOffice', val),
});

const selectedDepartment = computed({
  get: () => props.selectedDepartment,
  set: (val) => emit('update:selectedDepartment', val),
});

const selectedVehicle = computed({
  get: () => props.selectedVehicle,
  set: (val) => emit('update:selectedVehicle', val),
});

const dateRange = computed({
  get: () => props.dateRange,
  set: (val) => emit('update:dateRange', val),
});
</script>

<template>
  <div class="row v-theme--light align-items-center">
    <div class="col-3">
      <SingleOperationOfficeAutoComplete
        v-model="selectedOperationOffice"
        :clearable="true"
        selectFirstIfOnlyOne
      />
    </div>
    <div class="col-3">
      <DepartmentAutoComplete
        v-model="selectedDepartment"
        :operationOffices="selectedOperationOffice ? [selectedOperationOffice] : []"
        :selectFirstIfOnlyOne="false"
      />
    </div>
    <div class="col-2">
      <VehicleAutoComplete
        v-model="selectedVehicle"
        :selectedDepartment="selectedDepartment ?? null"
        :operationOffices="selectedOperationOffice ? [selectedOperationOffice] : []"
        :requestParams="{ type: 'filter' }"
      />
    </div>
    <div class="col-3">
      <RangeDatePicker
        v-model="dateRange"
        :maxRange="props.maxDateRange"
        format="yyyy/MM/dd"
        :isError="false"
      />
    </div>
    <div class="col-1 d-flex align-items-center">
      <v-btn
        class="exec-btn"
        :loading="props.loading"
        :disabled="!selectedVehicle || !selectedOperationOffice"
        @click="emit('search')"
      >
        {{ i18n.global.t('pages.common.button.exec') }}
      </v-btn>
    </div>
  </div>
</template>
